import React, { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from '@/hooks/useReducedMotion';

interface DnaHelixProps {
  className?: string;
}

export const DnaHelix: React.FC<DnaHelixProps> = ({ className = '' }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rotationRef = useRef(0);
  const reducedMotion = useReducedMotion();
  const [isInView, setIsInView] = useState(true);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.15 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (reducedMotion || !containerRef.current || !isInView) return;

    const container = containerRef.current;
    let animationId: number;

    const animate = () => {
      rotationRef.current += 0.25;
      container.style.transform = `rotateY(${rotationRef.current}deg)`;
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationId);
  }, [reducedMotion, isInView]);

  useEffect(() => {
    if (!reducedMotion || !containerRef.current) return;
    containerRef.current.style.transform = 'none';
  }, [reducedMotion]);

  // Generate helix structure
  const pairs = 24;
  const helixElements = [];

  for (let i = 0; i < pairs; i++) {
    const y = (i / pairs) * 100;
    const phase1 = (i / pairs) * Math.PI * 4;
    const phase2 = phase1 + Math.PI;
    
    const x1 = 50 + 35 * Math.sin(phase1);
    const x2 = 50 + 35 * Math.sin(phase2);
    const z1 = 35 * Math.cos(phase1);
    const z2 = 35 * Math.cos(phase2);
    
    // Calculate opacity based on z-depth for 3D effect
    const opacity1 = 0.3 + (z1 + 35) / 70 * 0.7;
    const opacity2 = 0.3 + (z2 + 35) / 70 * 0.7;
    
    // Glow intensity based on position
    const glow1 = Math.sin(phase1) > 0.5 ? '0 0 10px rgba(41, 151, 255, 0.8)' : 'none';
    const glow2 = Math.sin(phase2) > 0.5 ? '0 0 10px rgba(41, 151, 255, 0.8)' : 'none';

    helixElements.push(
      <React.Fragment key={i}>
        {/* Strand 1 nucleotide */}
        <div
          className="absolute w-3 h-3 rounded-full bg-apple-blue transition-all duration-300"
          style={{
            left: `${x1}%`,
            top: `${y}%`,
            opacity: opacity1,
            boxShadow: glow1,
            transform: `translateZ(${z1}px) scale(${0.5 + opacity1 * 0.5})`,
          }}
        />
        {/* Strand 2 nucleotide */}
        <div
          className="absolute w-3 h-3 rounded-full bg-apple-blue transition-all duration-300"
          style={{
            left: `${x2}%`,
            top: `${y}%`,
            opacity: opacity2,
            boxShadow: glow2,
            transform: `translateZ(${z2}px) scale(${0.5 + opacity2 * 0.5})`,
          }}
        />
        {/* Connection bar */}
        <div
          className="absolute h-0.5 bg-gradient-to-r from-apple-blue/50 to-apple-blue/30"
          style={{
            left: `${Math.min(x1, x2)}%`,
            top: `${y + 0.4}%`,
            width: `${Math.abs(x2 - x1)}%`,
            opacity: (opacity1 + opacity2) / 3,
            transform: `translateZ(${(z1 + z2) / 2}px)`,
          }}
        />
      </React.Fragment>
    );
  }

  return (
    <div 
      className={`relative ${className}`}
      style={{ 
        perspective: '1000px',
        transformStyle: 'preserve-3d',
      }}
    >
      {/* Glow effect background */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-64 h-96 bg-apple-blue/10 rounded-full blur-3xl" />
      </div>
      
      {/* Helix container */}
      <div
        ref={containerRef}
        className="relative w-full h-full"
        style={{
          transformStyle: 'preserve-3d',
          willChange: reducedMotion ? 'auto' : 'transform',
          transition: reducedMotion ? 'none' : undefined,
        }}
      >
        {helixElements}
      </div>
      
      {/* Additional glow rings */}
      {!reducedMotion && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-48 h-48 border border-apple-blue/20 rounded-full animate-pulse-ring" />
          <div className="absolute w-64 h-64 border border-apple-blue/10 rounded-full animate-pulse-ring" style={{ animationDelay: '0.5s' }} />
        </div>
      )}
    </div>
  );
};

export default DnaHelix;
